import { fetchApi } from '../client';
import type { PagedResponse } from '../../../types/pagination';

export type GrowthBand = 'LOW' | 'MEDIUM' | 'HIGH';

export interface GrowthFunnel {
  total: number;
  retentionLow: number;
  retentionMedium: number;
  retentionHigh: number;
  riskLow: number;
  riskMedium: number;
  riskHigh: number;
}

export interface GrowthProfile {
  patientId: string;
  patientName: string | null;
  lastVisitDate: string | null;
  visitCount12m: number;
  noShowCount12m: number;
  retentionScore: number;
  retentionBand: GrowthBand;
  retentionReasons: string[];
  riskBand: GrowthBand;
  riskFactors: string[];
  nbaCode: string;
  nbaLabel: string | null;
  nbaReason: string | null;
  nbaActionUrl: string | null;
  nbaRationaleAi: string | null;
  computedAt: string;
}

export interface PagedGrowth {
  content: GrowthProfile[];
  totalElements: number;
  totalPages: number;
  page: number;
  size: number;
}

export interface OneClickSummary {
  patientId: string;
  patientName: string;
  age: number | null;
  gender: string | null;
  allergies: string | null;
  chronicConditions: string | null;
  aiAvailable: boolean;
  aiBriefJson: string | null;
  templatedBrief: string;
  lastVisitDate: string | null;
  visitCount12m: number;
  retentionBand: GrowthBand | null;
  riskBand: GrowthBand | null;
  riskFactors: string[];
  nbaCode: string | null;
  nbaLabel: string | null;
  nbaReason: string | null;
  nbaActionUrl: string | null;
  nbaRationaleAi: string | null;
  disclaimer: string;
}

export type CampaignStatus = 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'COMPLETED';
export type CampaignSendMode = 'MANUAL_APPROVE' | 'AUTO';
export type CampaignScheduleType = 'ONE_OFF' | 'RECURRING';
export type RecipientStatus = 'PENDING' | 'APPROVED' | 'SENT' | 'SKIPPED';

export interface Campaign {
  id?: string;
  name: string;
  status?: CampaignStatus;
  segmentBand?: GrowthBand | null;
  segmentChronicOnly?: boolean;
  segmentLastVisitMinDays?: number | null;
  channels?: string;
  subject: string;
  bodyTemplate: string;
  sendMode?: CampaignSendMode;
  scheduleType?: CampaignScheduleType;
  lastRunAt?: string | null;
  createdAt?: string;
  recipientsPending?: number;
  recipientsSent?: number;
}

export interface CampaignRecipient {
  id: string;
  patientId: string;
  patientName: string | null;
  status: RecipientStatus;
  sentAt: string | null;
}

export const growthModule = {
  getFunnel: () => fetchApi<GrowthFunnel>('/api/growth/funnel'),

  getRetention: (band: GrowthBand | undefined, page = 0, size = 20) =>
    fetchApi<PagedGrowth>(
      `/api/growth/retention?page=${page}&size=${size}${band ? `&band=${band}` : ''}`),

  getActionQueue: (page = 0, size = 20) =>
    fetchApi<PagedGrowth>(`/api/growth/actions?page=${page}&size=${size}`),

  recompute: () =>
    fetchApi<{ scored: number }>('/api/growth/recompute', { method: 'POST' }),

  getProfile: (patientId: string) =>
    fetchApi<GrowthProfile>(`/api/growth/patients/${patientId}`),

  sendRecall: (patientId: string) =>
    fetchApi<{ sent: boolean }>(`/api/growth/actions/${patientId}/send-recall`, { method: 'POST' }),

  getSummary: (patientId: string) =>
    fetchApi<OneClickSummary>(`/api/growth/patients/${patientId}/summary`),

  // ── Campaigns ──
  listCampaigns: () => fetchApi<Campaign[]>('/api/growth/campaigns'),
  getCampaign: (id: string) => fetchApi<Campaign>(`/api/growth/campaigns/${id}`),
  createCampaign: (c: Campaign) =>
    fetchApi<Campaign>('/api/growth/campaigns', { method: 'POST', body: JSON.stringify(c) }),
  updateCampaign: (id: string, c: Campaign) =>
    fetchApi<Campaign>(`/api/growth/campaigns/${id}`, { method: 'PUT', body: JSON.stringify(c) }),
  deleteCampaign: (id: string) =>
    fetchApi<void>(`/api/growth/campaigns/${id}`, { method: 'DELETE' }),
  runCampaign: (id: string) =>
    fetchApi<{ sent: number }>(`/api/growth/campaigns/${id}/run`, { method: 'POST' }),
  listRecipients: (id: string) =>
    fetchApi<CampaignRecipient[]>(`/api/growth/campaigns/${id}/recipients`),
  /** One page of a campaign's recipients, waiting-for-approval first (medopd-docs 112). */
  listRecipientsPage: (id: string, page: number, size: number) =>
    fetchApi<PagedResponse<CampaignRecipient>>(`/api/growth/campaigns/${id}/recipients?page=${page}&size=${size}`),
  approveRecipient: (recipientId: string) =>
    fetchApi<{ sent: boolean }>(`/api/growth/campaigns/recipients/${recipientId}/approve`, { method: 'POST' }),
};
